import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Images from "../utils/Images";
import { toast } from "react-toastify";

const LOCATION_CACHE_KEY = "xpanse_current_location";
const CHECKLIST_SUBMIT_API = "https://opsapis.xpansecoffee.com/api/checklists/submissions";
const CHECKLIST_DRAFT_PREFIX = "xpanse_checklist_draft";

export default function ChecklistSubmit() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const topProgressRef = useRef(null);
    const draftHydratedRef = useRef(false);
    const [token, setToken] = useState(null);
    const [items, setItems] = useState([]);
    const [formData, setFormData] = useState(null);
    const [location, setLocation] = useState(null);
    const [isTopProgressVisible, setIsTopProgressVisible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState("");

    const stateLocation = useLocation();
    const categoryId = stateLocation.state?.checklistId;
    const checklistType = stateLocation.state?.checklistType;
    const frequency = stateLocation.state?.frequency;

    // const categoryId = searchParams.get("id") || searchParams.get("category_id");
    // const checklistType = searchParams.get("checklist_type") || "OPENING";
    // const frequency = searchParams.get("frequency") || "DAILY";
    const businessDate =
        searchParams.get("business_date") ||
        new Date().toISOString().split("T")[0];

    const activeCategoryId = useMemo(() => {
        const apiCategoryId =
            formData?.category_id ??
            formData?.categories?.[0]?.id;

        return Number(apiCategoryId ?? categoryId);
    }, [formData, categoryId]);

    const draftStorageKey = useMemo(() => {
        const resolvedChecklistType = formData?.checklist_type || checklistType;
        const resolvedFrequency = formData?.frequency || frequency;
        const resolvedBusinessDate = formData?.business_date || businessDate;

        if (
            !activeCategoryId ||
            !resolvedChecklistType ||
            !resolvedFrequency ||
            !resolvedBusinessDate
        ) {
            return null;
        }

        return [
            CHECKLIST_DRAFT_PREFIX,
            activeCategoryId,
            resolvedChecklistType,
            resolvedFrequency,
            resolvedBusinessDate,
        ].join(":");
    }, [
        activeCategoryId,
        formData,
        checklistType,
        frequency,
        businessDate,
    ]);

    useEffect(() => {
        const storedToken = localStorage.getItem("token");

        if (!storedToken) {
            navigate("/login", { replace: true });
            return;
        }

        setToken(storedToken);
    }, [navigate]);

    useEffect(() => {
        if (token) {
            fetchChecklistForm();
        }
    }, [token, categoryId, checklistType, frequency, businessDate]);

    const fetchChecklistForm = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `https://opsapis.xpansecoffee.com/api/checklists/form?category_id=${categoryId}&checklist_type=${checklistType}&frequency=${frequency}&business_date=${businessDate}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                }
            );

            const json = await response.json();

            if (!response.ok || !json.success) {
                throw new Error(json.message || "Failed to fetch checklist");
            }

            const data = json.data;
            setFormData(data);

            const mappedItems = data.categories.flatMap((category) =>
                category.tasks.map((task) => ({
                    id: task.id,
                    category_id: category.id,
                    title: task.task_name,
                    description: category.category_name,
                    answer: task.answer || "",
                    remark: task.remark || "",
                    is_mandatory: task.is_mandatory,
                    sort_order: task.sort_order,
                }))
            );

            const resolvedCategoryId = Number(
                data?.category_id ?? data?.categories?.[0]?.id ?? categoryId
            );
            const resolvedDraftKey = [
                CHECKLIST_DRAFT_PREFIX,
                resolvedCategoryId,
                data?.checklist_type || checklistType,
                data?.frequency || frequency,
                data?.business_date || businessDate,
            ].join(":");

            let restoredItems = mappedItems;

            if (!data?.read_only) {
                try {
                    const cachedDraft = localStorage.getItem(resolvedDraftKey);

                    if (cachedDraft) {
                        const parsedDraft = JSON.parse(cachedDraft);
                        const draftAnswers = new Map(
                            (parsedDraft?.items || []).map((item) => [
                                Number(item.id),
                                item,
                            ])
                        );

                        restoredItems = mappedItems.map((item) => {
                            const draftItem = draftAnswers.get(Number(item.id));

                            return draftItem
                                ? {
                                    ...item,
                                    answer: draftItem.answer || "",
                                    remark: draftItem.remark || "",
                                }
                                : item;
                        });
                    }
                } catch {
                    localStorage.removeItem(resolvedDraftKey);
                }
            }

            setItems(restoredItems);
            draftHydratedRef.current = true;
        } catch (error) {
            toast.error(error.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (
            !draftHydratedRef.current ||
            !draftStorageKey ||
            loading ||
            formData?.read_only
        ) {
            return;
        }

        const hasDraftContent = items.some(
            (item) => item.answer || item.remark.trim()
        );

        if (!hasDraftContent) {
            localStorage.removeItem(draftStorageKey);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            localStorage.setItem(
                draftStorageKey,
                JSON.stringify({
                    category_id: activeCategoryId,
                    checklist_type: formData?.checklist_type || checklistType,
                    frequency: formData?.frequency || frequency,
                    business_date: formData?.business_date || businessDate,
                    updated_at: new Date().toISOString(),
                    items: items.map(({ id, category_id, answer, remark }) => ({
                        id,
                        category_id,
                        answer,
                        remark,
                    })),
                })
            );
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [
        items,
        loading,
        formData,
        draftStorageKey,
        activeCategoryId,
        checklistType,
        frequency,
        businessDate,
    ]);

    useEffect(() => {
        try {
            const cached = localStorage.getItem(LOCATION_CACHE_KEY);
            if (cached) {
                setLocation(JSON.parse(cached));
            }
        } catch {
            setLocation(null);
        }
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (!topProgressRef.current) return;

            const rect = topProgressRef.current.getBoundingClientRect();

            setIsTopProgressVisible(rect.bottom > 100);
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const answeredCount = useMemo(
        () => items.filter((item) => item.answer).length,
        [items]
    );

    const progress = items.length
        ? Math.round((answeredCount / items.length) * 100)
        : 0;

    const canSubmit = useMemo(() => {
        if (!items.length) return false;

        return items.every((item) => {
            if (item.is_mandatory === 1 && !item.answer) return false;
            if (item.answer === "NO" && !item.remark.trim()) return false;
            return true;
        });
    }, [items]);

    const updateAnswer = (id, answer) => {
        if (formData?.read_only) return;

        setItems((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        answer: item.answer === answer ? "" : answer,
                    }
                    : item
            )
        );
    };

    const updateRemark = (id, remark) => {
        if (formData?.read_only) return;

        setItems((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, remark } : item
            )
        );
    };

    const buildPayload = (status) => {
        const answeredItems =
            status === "DRAFT"
                ? items.filter((item) => item.answer)
                : items;

        return {
            checklist_type: formData?.checklist_type || checklistType,
            frequency: formData?.frequency || frequency,
            business_date: formData?.business_date || businessDate,
            status,
            latitude: location?.latitude ?? null,
            longitude: location?.longitude ?? null,
            address: location?.name || location?.address || null,
            category_id: activeCategoryId,
            category_complete: status === "SUBMITTED",
            all_categories_complete:
                status === "SUBMITTED"
                    ? Boolean(formData?.all_categories_complete)
                    : false,
            answers: answeredItems.map((item) => ({
                category_id: item.category_id,
                task_id: item.id,
                answer: item.answer,
                remark: item.remark.trim() || null,
            })),
        };
    };

    const saveChecklist = async (status) => {
        if (!token || formData?.read_only || actionLoading) return;

        if (!activeCategoryId) {
            toast.error("Checklist category is missing. Please reopen the checklist.");
            return;
        }

        if (
            status === "DRAFT" &&
            !items.some((item) => item.answer || item.remark.trim())
        ) {
            toast.error("Please fill at least one checklist item before saving a draft.");
            return;
        }

        if (status === "SUBMITTED") {
            const missingMandatoryItem = items.find(
                (item) => item.is_mandatory === 1 && !item.answer
            );

            if (missingMandatoryItem) {
                toast.error(
                    `Please answer "${missingMandatoryItem.title}" before submitting.`
                );
                return;
            }

            const invalidItem = items.find(
                (item) => item.answer === "NO" && !item.remark.trim()
            );

            if (invalidItem) {
                toast.error(
                    `Please enter a remark for "${invalidItem.title}".`
                );
                return;
            }
        }

        try {
            setActionLoading(status);
            const payload = buildPayload(status);

            if (status === "DRAFT" && draftStorageKey) {
                localStorage.setItem(
                    draftStorageKey,
                    JSON.stringify({
                        category_id: activeCategoryId,
                        checklist_type: payload.checklist_type,
                        frequency: payload.frequency,
                        business_date: payload.business_date,
                        updated_at: new Date().toISOString(),
                        items: items.map(({ id, category_id, answer, remark }) => ({
                            id,
                            category_id,
                            answer,
                            remark,
                        })),
                    })
                );
            }

            const response = await fetch(CHECKLIST_SUBMIT_API, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const json = await response.json().catch(() => ({}));

            if (!response.ok || json.success === false) {
                throw new Error(
                    json.message ||
                    `Unable to ${status === "DRAFT" ? "save draft" : "submit checklist"}`
                );
            }

            toast.success(
                json.message ||
                (status === "DRAFT"
                    ? "Draft saved successfully."
                    : "Checklist submitted successfully.")
            );
            if (status === "SUBMITTED") {
                if (draftStorageKey) {
                    localStorage.removeItem(draftStorageKey);
                }
                draftHydratedRef.current = false;
                navigate("/home", { replace: true });
            } else {
                await fetchChecklistForm();
            }
        } catch (error) {
            const message =
                error.message ||
                (status === "DRAFT"
                    ? "Unable to save draft."
                    : "Unable to submit checklist.");

            toast.error(message);
        } finally {
            setActionLoading("");
        }
    };

    const handleSaveDraft = () => saveChecklist("DRAFT");
    const handleSubmit = () => saveChecklist("SUBMITTED");

    const remainingTasks = items.length - answeredCount;
    const showBottomProgress = !isTopProgressVisible && remainingTasks > 0;

    if (loading) {
        return (
            <main className="min-h-screen bg-[#F6FBFD] flex items-center justify-center">
                <p className="text-[#09A5F7] font-black">Loading checklist...</p>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-gradient-to-b from-[#18c2dc] via-[#08a8cc] to-[#017fa8] flex justify-center font-['Roboto']">
            <section className="relative w-full max-w-[430px] min-h-screen bg-[#F6FBFD] shadow-[0_24px_70px_rgba(14,73,97,0.26)] overflow-hidden">
                <div className="sticky top-0 z-30 bg-[#09A5F7]/95 backdrop-blur-xl border-b border-[#4FC8FF]/70 rounded-b-[30px] shadow-[0_14px_35px_rgba(9,165,247,0.28)]">
                    <div className="flex items-center justify-between px-5 py-4">
                        <img
                            src={Images.logo}
                            alt="Xpanse Logo"
                            width="118"
                            height="48"
                            className="object-contain"
                        />

                        <button
                            onClick={() => navigate("/")}
                            type="button"
                            className="w-11 h-11 rounded-full bg-white/95 border border-white/70 flex items-center justify-center text-[#09A5F7] text-xl shadow-sm active:scale-95 transition"
                        >
                            ↪
                        </button>
                    </div>
                </div>

                <div className="px-5 pt-5 pb-32">
                    <button
                        type="button"
                        onClick={() => navigate("/home")}
                        className="text-[#09A5F7] text-sm font-extrabold mb-5 active:scale-95 transition"
                    >
                        ← Back
                    </button>

                    <div className="rounded-[30px] bg-gradient-to-br from-white to-[#EAF8FE] border border-[#DDF0F6] p-5 shadow-[0_18px_42px_rgba(14,73,97,0.12)]">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#09A5F7] font-black">
                            {formData?.checklist_type} Checklist
                        </p>

                        <h1 className="text-[23px] font-black text-[#0E4961] leading-tight mt-2">
                            {formData?.category_name}
                        </h1>

                        <div className="mt-5 flex items-center justify-between gap-3">
                            <div className="rounded-2xl bg-white px-4 py-3 border border-[#DDF0F6] flex-1">
                                <p className="text-[11px] text-[#6E8794] font-bold">
                                    Business Date
                                </p>
                                <p className="text-sm text-[#0E4961] font-black mt-1">
                                    {formData?.business_date}
                                </p>
                            </div>

                            <div className="rounded-2xl bg-white px-4 py-3 border border-[#DDF0F6] flex-1">
                                <p className="text-[11px] text-[#6E8794] font-bold">
                                    Status
                                </p>
                                <p className="text-sm text-[#0E4961] font-black mt-1">
                                    {formData?.status}
                                </p>
                            </div>
                        </div>
                    </div>

                    {location?.name && (
                        <div className="mt-4 rounded-[22px] bg-white border border-[#DDF0F6] px-4 py-3 shadow-sm">
                            <p className="text-[11px] uppercase tracking-wide text-[#6E8794] font-extrabold">
                                Current Location
                            </p>
                            <p className="text-xs text-[#0E4961] font-bold mt-1 leading-relaxed">
                                📍 {location.name}
                            </p>
                        </div>
                    )}

                    <div
                        ref={topProgressRef}
                        className="mt-4 rounded-[26px] bg-white border border-[#DDF0F6] p-4 shadow-[0_16px_36px_rgba(14,73,97,0.10)]"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[11px] uppercase tracking-wide text-[#6E8794] font-extrabold">
                                    Checklist Progress
                                </p>
                                <p className="text-[#0E4961] font-black text-sm mt-1">
                                    {answeredCount} of {items.length} checks completed
                                </p>
                            </div>

                            <div className="relative w-16 h-16 rounded-full bg-[#EAF8FE] flex items-center justify-center text-[#09A5F7] font-black border border-[#CDEEF9]">
                                {progress}%
                            </div>
                        </div>

                        <div className="mt-4 h-3 rounded-full bg-[#E6F3F8] overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[#09A5F7] to-[#18c2dc] rounded-full transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    <div className="mt-5 space-y-4">
                        {items.map((item, index) => {
                            const isNoInvalid =
                                item.answer === "NO" && !item.remark.trim();

                            return (
                                <div
                                    key={item.id}
                                    className="rounded-[28px] bg-white border border-[#DDF0F6] shadow-[0_16px_36px_rgba(14,73,97,0.10)] p-4"
                                >
                                    <div className="flex gap-3">
                                        <div
                                            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-black shrink-0 ${item.answer
                                                ? "bg-[#09A5F7] text-white"
                                                : "bg-[#EAF8FE] text-[#09A5F7]"
                                                }`}
                                        >
                                            {item.answer ? "✓" : index + 1}
                                        </div>

                                        <div className="flex-1">
                                            <h3 className="text-[#0E4961] text-[15px] font-extrabold leading-snug">
                                                {item.title}
                                                {item.is_mandatory === 1 && (
                                                    <span className="text-red-500"> *</span>
                                                )}
                                            </h3>

                                            <p className="text-[#6E8794] text-xs font-medium mt-2 leading-relaxed">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 mt-4">
                                        <button
                                            type="button"
                                            disabled={formData?.read_only}
                                            onClick={() => updateAnswer(item.id, "YES")}
                                            className={`h-12 rounded-[17px] border text-sm font-black transition active:scale-[0.98] ${item.answer === "YES"
                                                ? "bg-green-500 border-green-500 text-white"
                                                : "bg-green-50 border-green-200 text-green-600"
                                                }`}
                                        >
                                            YES
                                        </button>

                                        <button
                                            type="button"
                                            disabled={formData?.read_only}
                                            onClick={() => updateAnswer(item.id, "NO")}
                                            className={`h-12 rounded-[17px] border text-sm font-black transition active:scale-[0.98] ${item.answer === "NO"
                                                ? "bg-red-500 border-red-500 text-white"
                                                : "bg-red-50 border-red-200 text-red-500"
                                                }`}
                                        >
                                            NO
                                        </button>
                                    </div>

                                    <div className="mt-4">
                                        <label className="text-xs text-[#0E4961] font-extrabold">
                                            Remark{" "}
                                            {item.answer === "NO" ? (
                                                <span className="text-red-500">*</span>
                                            ) : (
                                                <span className="text-[#6E8794]">(optional)</span>
                                            )}
                                        </label>

                                        <textarea
                                            value={item.remark}
                                            disabled={formData?.read_only}
                                            onChange={(e) =>
                                                updateRemark(item.id, e.target.value)
                                            }
                                            placeholder={
                                                item.answer === "NO"
                                                    ? "Remark is required for NO response"
                                                    : "Add remark if needed"
                                            }
                                            rows={3}
                                            className={`mt-2 w-full resize-none rounded-[20px] border bg-[#FBFEFF] px-4 py-3 text-sm text-[#0E4961] outline-none placeholder:text-[#8FA5AF] transition ${isNoInvalid
                                                ? "border-red-300 focus:border-red-500"
                                                : "border-[#DDF0F6] focus:border-[#09A5F7]"
                                                }`}
                                        />

                                        {isNoInvalid && (
                                            <p className="mt-2 text-xs font-bold text-red-500">
                                                Remark is required when NO is selected.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] bg-white/95 backdrop-blur-xl border-t border-[#DDF0F6] px-5 py-4">
                    <div className="flex items-center gap-3">

                        <div className={`
                                relative overflow-hidden transition-all duration-500 ease-in-out
                                ${showBottomProgress
                                ? "w-14 opacity-100 mr-0"
                                : "w-0 opacity-0 -mr-3"}
                            `}>
                            <div className="relative w-14 h-14 rounded-full bg-[#EAF8FE] border border-[#CDEEF9] flex items-center justify-center shrink-0">
                                <svg className="absolute inset-0 w-full h-full -rotate-90">
                                    <circle
                                        cx="28"
                                        cy="28"
                                        r="23"
                                        stroke="#ffffff"
                                        strokeWidth="5"
                                        fill="none"
                                    />
                                    <circle
                                        cx="28"
                                        cy="28"
                                        r="23"
                                        stroke="#09A5F7"
                                        strokeWidth="5"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeDasharray={2 * Math.PI * 23}
                                        strokeDashoffset={
                                            2 * Math.PI * 23 -
                                            (progress / 100) * (2 * Math.PI * 23)
                                        }
                                        className="transition-all duration-900 ease-in-out"
                                    />
                                </svg>

                                <div className="relative z-10 text-center">
                                    <p className="text-[#09A5F7] text-sm font-black leading-none transition-all duration-300">
                                        {remainingTasks}
                                    </p>
                                    <p className="text-[8px] text-[#6E8794] font-bold mt-0.5">
                                        left
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 flex gap-3">
                            <button
                                type="button"
                                onClick={handleSaveDraft}
                                disabled={formData?.read_only || Boolean(actionLoading)}
                                className="flex-1 h-14 rounded-[18px] border-2 border-[#09A5F7] bg-white text-[#09A5F7] font-black text-sm disabled:opacity-60"
                            >
                                {actionLoading === "DRAFT" ? "Saving..." : "Save"}
                            </button>

                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={
                                    !canSubmit ||
                                    formData?.read_only ||
                                    Boolean(actionLoading)
                                }
                                className={`flex-[1.3] h-14 rounded-[18px] font-black text-base transition-all ${canSubmit && !formData?.read_only && !actionLoading
                                    ? "bg-gradient-to-r from-[#09A5F7] to-[#18c2dc] text-white"
                                    : "bg-[#F1FAFD] text-[#8FA5AF] border border-[#DDF0F6]"
                                    }`}
                            >
                                {actionLoading === "SUBMITTED"
                                    ? "Submitting..."
                                    : "Submit ✓"}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}